/**
 * XorPixViewer - 暗号化された画像を復号して表示するコンポーネント
 *
 * セキュリティ対策:
 * - Blob URLを使わずImageDataを直接Canvasに描画
 * - ネットワークタブに復号後の画像が表示されない
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { combineSecureXorSharesWebGL } from '../../utils/xorShareGenerator';
import { displayStateManager } from '../../utils/displayStateManager';
import { Loader2 } from 'lucide-react';
import { config } from '../../config';

interface XorPixViewerProps {
  /** ノイズレイヤー1のURL（CDN推奨） */
  noise1Url: string;
  /** ノイズレイヤー2のURL（CDN推奨） */
  noise2Url: string;
  /** リソースID（暗号化シード） */
  rid: string;
  /** 画像の幅 */
  width: number;
  /** 画像の高さ */
  height: number;
  /** キャラクターID（キャッシュ用、オプション） */
  characterId?: string;
  /** フォールバック用ノイズ1 URL（GCS署名付きURL） */
  fallbackNoise1Url?: string;
  /** フォールバック用ノイズ2 URL（GCS署名付きURL） */
  fallbackNoise2Url?: string;
  /** クラス名 */
  className?: string;
  /** フィットモード */
  fitMode?: 'contain' | 'cover';
  /** ロード完了コールバック */
  onLoad?: () => void;
  /** エラーコールバック */
  onError?: (error: Error) => void;
  /** クリックコールバック */
  onClick?: () => void;
  /** 表示保護を有効にするか */
  enableProtection?: boolean;
}

const XorPixViewer: React.FC<XorPixViewerProps> = ({
  noise1Url,
  noise2Url,
  rid,
  width,
  height,
  characterId,
  fallbackNoise1Url,
  fallbackNoise2Url,
  className = '',
  fitMode = 'cover',
  onLoad,
  onError,
  onClick,
  enableProtection = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const originalImageDataRef = useRef<ImageData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, setIsProtected] = useState(false);
  // 処理済みのキャラクターIDを追跡（重複処理防止）
  const processedRef = useRef<string | null>(null);
  // 処理中フラグ（同時実行防止）
  const isProcessingRef = useRef(false);

  /**
   * 保護モード時にCanvasに透かしを描画
   */
  const applyProtection = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !originalImageDataRef.current) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.putImageData(originalImageDataRef.current, 0, 0);

    // 半透明の暗いオーバーレイ
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 繰り返し透かしテキストを描画
    const text = 'ORIFOLI';
    const fontSize = Math.max(10, Math.min(canvas.width, canvas.height) / 25);
    const letterSpacing = fontSize * 0.5;
    ctx.font = `300 ${fontSize}px "Helvetica Neue", Arial, sans-serif`;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const drawSpacedText = (str: string, x: number, y: number) => {
      const chars = str.split('');
      const totalWidth = chars.length * (fontSize * 0.6 + letterSpacing);
      let currentX = x - totalWidth / 2;
      for (const char of chars) {
        ctx.fillText(char, currentX, y);
        currentX += fontSize * 0.6 + letterSpacing;
      }
    };

    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(-Math.PI / 6);

    const rowSpacing = fontSize * 6;
    const colSpacing = fontSize * 10;
    const diagonal = Math.sqrt(canvas.width ** 2 + canvas.height ** 2);
    const rows = Math.ceil(diagonal / rowSpacing) + 2;
    const cols = Math.ceil(diagonal / colSpacing) + 2;

    for (let row = -rows; row <= rows; row++) {
      for (let col = -cols; col <= cols; col++) {
        drawSpacedText(text, col * colSpacing, row * rowSpacing);
      }
    }

    ctx.restore();
    setIsProtected(true);
  }, []);

  /**
   * 保護モード解除時に元の画像を復元
   */
  const removeProtection = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !originalImageDataRef.current) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.putImageData(originalImageDataRef.current, 0, 0);
    setIsProtected(false);
  }, []);

  /**
   * 表示状態管理の登録
   */
  useEffect(() => {
    if (!enableProtection) return;

    displayStateManager.init({}, {
      protectionDuration: 3000,
      protectOnBlur: true,
      protectOnVisibilityChange: true,
      detectKeyEvents: true,
      debug: false
    });

    const listenerId = displayStateManager.register({
      onProtectionTriggered: () => {
        applyProtection();
      },
      onProtectionReleased: () => {
        removeProtection();
      }
    });

    return () => {
      displayStateManager.unregister(listenerId);
    };
  }, [enableProtection, applyProtection, removeProtection]);

  // IndexedDBを開く共通関数
  const openCacheDB = useCallback((): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('orifoliXorCache', 1);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      request.onupgradeneeded = (event) => {
        const database = (event.target as IDBOpenDBRequest).result;
        if (!database.objectStoreNames.contains('noiseImages')) {
          const store = database.createObjectStore('noiseImages', { keyPath: 'id' });
          store.createIndex('characterId', 'characterId', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }, []);

  // キャッシュから読み込む
  const loadFromCache = useCallback(async (charId: string): Promise<{ noise1: Blob; noise2: Blob } | null> => {
    try {
      const db = await openCacheDB();
      const transaction = db.transaction('noiseImages', 'readonly');
      const store = transaction.objectStore('noiseImages');

      // ORIFOLI専用プレフィックスでキャッシュキーを生成（Figryneとの衝突防止）
      const cacheKeyPrefix = `orifoli_${charId}`;

      const noise1Promise = new Promise<any>((resolve, reject) => {
        const request = store.get(`${cacheKeyPrefix}_noise1`);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      const noise2Promise = new Promise<any>((resolve, reject) => {
        const request = store.get(`${cacheKeyPrefix}_noise2`);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      const [noise1Data, noise2Data] = await Promise.all([noise1Promise, noise2Promise]);

      if (noise1Data?.blob && noise2Data?.blob) {
        // キャッシュ有効期限チェック（7日間）
        const CACHE_EXPIRY = 7 * 24 * 60 * 60 * 1000;
        const now = Date.now();
        if (now - noise1Data.timestamp < CACHE_EXPIRY && now - noise2Data.timestamp < CACHE_EXPIRY) {
          return { noise1: noise1Data.blob, noise2: noise2Data.blob };
        }
      }

      return null;
    } catch {
      return null;
    }
  }, [openCacheDB]);

  // キャッシュに保存
  const saveToCache = useCallback(async (charId: string, noise1Blob: Blob, noise2Blob: Blob) => {
    try {
      const db = await openCacheDB();
      const transaction = db.transaction('noiseImages', 'readwrite');
      const store = transaction.objectStore('noiseImages');
      const timestamp = Date.now();

      // ORIFOLI専用プレフィックスでキャッシュキーを生成（Figryneとの衝突防止）
      const cacheKeyPrefix = `orifoli_${charId}`;

      store.put({
        id: `${cacheKeyPrefix}_noise1`,
        characterId: charId,
        type: 'noise1',
        blob: noise1Blob,
        timestamp
      });
      store.put({
        id: `${cacheKeyPrefix}_noise2`,
        characterId: charId,
        type: 'noise2',
        blob: noise2Blob,
        timestamp
      });
    } catch {
      // キャッシュ書き込みエラーは無視
    }
  }, [openCacheDB]);

  const decryptAndRender = useCallback(async () => {
    if (!noise1Url || !noise2Url || !rid) {
      return;
    }

    // 同じキャラクターで既に処理済みならスキップ（重複処理防止）
    const cacheKey = `${characterId}_${rid}`;
    if (processedRef.current === cacheKey) {
      return;
    }

    // 同時実行防止
    if (isProcessingRef.current) {
      return;
    }
    isProcessingRef.current = true;

    setIsLoading(true);
    setError(null);

    try {
      let noise1Blob: Blob | null = null;
      let noise2Blob: Blob | null = null;

      // 1. まずキャッシュを確認
      if (characterId) {
        const cached = await loadFromCache(characterId);
        if (cached) {
          noise1Blob = cached.noise1;
          noise2Blob = cached.noise2;
        }
      }

      // 2. キャッシュになければネットワークから取得
      if (!noise1Blob || !noise2Blob) {
        let noise1Response: Response;
        let noise2Response: Response;
        let usedFallback = false;

        // dev/local環境ではCDNを使わず、GCS署名付きURLを使用
        if (!config.useCdn) {
          // dev環境: フォールバックURL（GCS署名付き）があればそれを使用
          // なければプライマリURLを使用（バックエンド更新後はGCS URLになる）
          const url1 = fallbackNoise1Url || noise1Url;
          const url2 = fallbackNoise2Url || noise2Url;

          [noise1Response, noise2Response] = await Promise.all([
            fetch(url1),
            fetch(url2)
          ]);

          if (!noise1Response.ok || !noise2Response.ok) {
            throw new Error('画像の取得に失敗しました');
          }
        } else {
          // 本番環境: CDN URLを先に試行、失敗したらフォールバック
          try {
            [noise1Response, noise2Response] = await Promise.all([
              fetch(noise1Url),
              fetch(noise2Url)
            ]);

            if (!noise1Response.ok || !noise2Response.ok) {
              throw new Error('CDN fetch failed');
            }
          } catch (cdnError) {
            // CDN失敗時、フォールバックURLがあれば使用
            if (fallbackNoise1Url && fallbackNoise2Url) {
              // console.log('[XorPixViewer] CDN failed, trying fallback URLs');
              [noise1Response, noise2Response] = await Promise.all([
                fetch(fallbackNoise1Url),
                fetch(fallbackNoise2Url)
              ]);
              usedFallback = true;

              if (!noise1Response.ok || !noise2Response.ok) {
                throw new Error('画像の取得に失敗しました');
              }
            } else {
              throw new Error('画像の取得に失敗しました');
            }
          }
        }

        const [fetchedNoise1, fetchedNoise2] = await Promise.all([
          noise1Response.blob(),
          noise2Response.blob()
        ]);
        noise1Blob = fetchedNoise1;
        noise2Blob = fetchedNoise2;

        // 3. フェッチ成功したらキャッシュに保存（フォールバック使用時はスキップ）
        if (!usedFallback && characterId) {
          // バックグラウンドで保存（awaitしない）
          saveToCache(characterId, noise1Blob, noise2Blob);
        }
      }

      // この時点でnoise1Blob, noise2Blobは必ず存在する
      if (!noise1Blob || !noise2Blob) {
        throw new Error('画像データの取得に失敗しました');
      }

      // WebGL（GPU）で高速合成
      const { imageData, width: imgWidth, height: imgHeight } = await combineSecureXorSharesWebGL(
        noise1Blob,
        noise2Blob,
        rid
      );

      // Canvasに直接ImageDataを描画（Blob URLを使わない）
      const canvas = canvasRef.current;
      if (!canvas) {
        throw new Error('Canvas not available');
      }

      canvas.width = imgWidth;
      canvas.height = imgHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas context not available');
      }

      // putImageDataで直接描画 - ネットワークタブに表示されない
      ctx.putImageData(imageData, 0, 0);

      // 保護モード用に元画像を保存
      if (enableProtection) {
        originalImageDataRef.current = ctx.getImageData(0, 0, imgWidth, imgHeight);
      }

      // 処理完了をマーク
      processedRef.current = cacheKey;
      isProcessingRef.current = false;

      setIsLoading(false);
      onLoad?.();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '復号に失敗しました';
      setError(errorMessage);
      setIsLoading(false);
      isProcessingRef.current = false;
      onError?.(err instanceof Error ? err : new Error(errorMessage));
    }
  }, [noise1Url, noise2Url, rid, characterId, fallbackNoise1Url, fallbackNoise2Url, onLoad, onError, loadFromCache, saveToCache, enableProtection]);

  // 初回マウント時のみ実行
  useEffect(() => {
    decryptAndRender();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noise1Url, noise2Url, rid, characterId]);

  // クリーンアップ: コンポーネントアンマウント時のみcanvasリソースを解放
  useEffect(() => {
    return () => {
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
      }
      // アンマウント時にrefsをリセット
      processedRef.current = null;
      isProcessingRef.current = false;
    };
  }, []);

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={{
        aspectRatio: `${width} / ${height}`,
      }}
      onClick={onClick}
    >
      {/* ローディング表示 */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 dark:bg-gray-800">
          <Loader2 className="w-6 h-6 text-gray-400 dark:text-gray-500 animate-spin" />
        </div>
      )}

      {/* エラー表示 */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
          <p className="text-gray-400 dark:text-gray-500 text-xs">{error}</p>
        </div>
      )}

      {/* Canvas - 右クリック/ドラッグ防止 */}
      <canvas
        ref={canvasRef}
        className={`w-full h-full ${fitMode === 'contain' ? 'object-contain' : 'object-cover'}`}
        style={{
          display: isLoading || error ? 'none' : 'block',
          // 画像保存防止
          pointerEvents: onClick ? 'auto' : 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
      />
    </div>
  );
};

export default XorPixViewer;
